import React from 'react';
import { Meeting } from './types';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

interface Props {
  meetings: Meeting[];
  selectedId: string | null;
  onSelect: (m: Meeting) => void;
}

export const MeetingsTable: React.FC<Props> = ({ meetings, selectedId, onSelect }) => {
  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(meetings);
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'PROPOSED':
        return <span className="badge-pill badge-info">Proposed</span>;
      case 'SCHEDULED':
        return <span className="badge-pill badge-info">Scheduled</span>;
      case 'INVITATIONS_SENT':
        return <span className="badge-pill badge-info">Invitations Sent</span>;
      case 'PREPARATION':
        return <span className="badge-pill badge-warning">Preparation</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-warning">In Progress</span>;
      case 'COMPLETED':
        return <span className="badge-pill badge-success">Completed</span>;
      case 'MINUTES_DRAFT':
        return <span className="badge-pill badge-warning">Minutes Draft</span>;
      case 'MINUTES_REVIEW':
        return <span className="badge-pill badge-info">Minutes Review</span>;
      case 'MINUTES_APPROVED':
        return <span className="badge-pill badge-success">Minutes Approved</span>;
      case 'FOLLOW_UP':
        return <span className="badge-pill badge-info">Follow-Up</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      case 'CANCELLED':
        return <span className="badge-pill badge-danger">Cancelled</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="meeting_number" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Meeting No
              </SortableTh>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Title
              </SortableTh>
              <SortableTh field="planned_start_time" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Date & Time
              </SortableTh>
              <SortableTh field="location" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Location / Channel
              </SortableTh>
              <SortableTh field="status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Status
              </SortableTh>
              <SortableTh field="chair_name" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Chair
              </SortableTh>
              <SortableTh field="organizer_name" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Organizer
              </SortableTh>
              <SortableTh field="quorum_achieved" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Quorum
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {meetings.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                  No meetings found.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => onSelect(item)}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === item.id ? '#f0f9ff' : 'transparent',
                  }}
                >
                  <td>
                    <span style={{ fontWeight: 600, color: '#0284c7', fontFamily: 'monospace' }}>
                      {item.meeting_number}
                    </span>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>v{item.version}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {item.target_department} {item.is_management_review ? '• [Management Review]' : ''}
                    </div>
                  </td>
                  <td style={{ fontSize: '12px', color: '#334155', whiteSpace: 'nowrap' }}>
                    {item.planned_start_time.replace('T', ' ').slice(0, 16)}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '12px' }}>
                    {item.location || (item.is_virtual ? 'Virtual (Video)' : 'Not specified')}
                  </td>
                  <td>
                    {getStatusBadge(item.status)}
                  </td>
                  <td style={{ color: '#334155', fontSize: '12px', fontWeight: 500 }}>
                    {item.chair_name}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '12px' }}>
                    {item.organizer_name}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {item.quorum_required ? (
                      item.quorum_achieved ? (
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Achieved</span>
                      ) : (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>✖ Not Achieved</span>
                      )
                    ) : (
                      <span style={{ color: '#94a3b8' }}>N/A</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(item);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '4px 10px', fontSize: '11px' }}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="meetings"
      />
    </div>
  );
};
